// oxlint-disable-next-line import/no-unresolved
import { useRegisterSW } from "virtual:pwa-register/react";
import clsx from "clsx";
import { Alert, Snackbar, SnackbarCloseReason } from "@mui/material";
import { useEffect, useRef } from "react";

// hooks
import { useConnectivityStatus } from "../../contexts/connectivity.context";

// utilities
import { Time } from "wombat-global/src/utilities/time";

// components
import { Banner } from "../Banner";
import Button from "../Buttons";
import { startServiceWorkerUpdatePolling } from "./service-worker-update-polling";

function useRegisterSWWithUpdatePolling(intervalMs: number) {
  const stopPollingRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    return () => {
      stopPollingRef.current?.();
    };
  }, []);

  return useRegisterSW({
    onRegisteredSW(scriptUrl, registration) {
      // oxlint-disable-next-line no-console
      console.log("SW Registered: " + scriptUrl, registration);
      stopPollingRef.current?.();
      stopPollingRef.current = registration
        ? startServiceWorkerUpdatePolling(registration, intervalMs)
        : undefined;
    },
    onRegisterError(error) {
      // oxlint-disable-next-line no-console
      console.log("SW registration error", error);
    },
  });
}

export function ReloadPromptInline({
  className,
}: React.HTMLAttributes<HTMLDivElement>) {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSWWithUpdatePolling(5 * Time.mins);

  return (
    <>
      {needRefresh && (
        <Button
          variant="text"
          size="small"
          color="info"
          className={clsx("text-[var(--color-gray--500)] mr-6", className)}
          onClick={() => updateServiceWorker(true)}
        >
          Click to Update App
        </Button>
      )}
    </>
  );
}

function ReloadPrompt() {
  const online = useConnectivityStatus();
  const {
    offlineReady: [offlineReady, setOfflineReady],
  } = useRegisterSWWithUpdatePolling(15 * Time.mins);

  const close = (
    // oxlint-disable-next-line
    ev: Event | React.SyntheticEvent<any, Event>,
    reason: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway" || reason === "escapeKeyDown") {
      return;
    }
    setOfflineReady(false);
  };

  return (
    <>
      {!online && (
        <Banner dismissible placement="bottom" style={{ padding: 0 }} spacer>
          <Alert severity="info" variant="standard">
            You have lost connectivity. Some features may not be available
          </Alert>
        </Banner>
      )}
      <Snackbar
        id="offline-snackbar"
        open={offlineReady}
        onClose={close}
        message={"App ready to work offline"}
        action={
          <Button
            variant="text"
            size="small"
            name="close-snackbar"
            onClick={() => {
              setOfflineReady(false);
            }}
          >
            Close
          </Button>
        }
      />
    </>
  );
}

export default ReloadPrompt;
